import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import {
  Trophy, ArrowsLeftRight, ChartLineUp, Heart, CalendarCheck,
  Crown, Medal, Funnel, CaretRight, Buildings, Briefcase
} from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";

const CATEGORIES = [
  { key: "referrals_given", label: "Referrals Given", icon: ArrowsLeftRight, gradient: "from-violet-400 to-purple-500", color: "text-violet-500" },
  { key: "business_given", label: "Business Given", icon: ChartLineUp, gradient: "from-teal-400 to-emerald-500", color: "text-emerald-500" },
  { key: "business_earned", label: "Business Earned", icon: Crown, gradient: "from-amber-400 to-orange-500", color: "text-amber-500" },
  { key: "visitors_invited", label: "Visitors Invited", icon: Heart, gradient: "from-pink-400 to-rose-500", color: "text-pink-500" },
  { key: "attendance", label: "Attendance", icon: CalendarCheck, gradient: "from-sky-400 to-blue-500", color: "text-sky-500" },
];

const PERIOD_OPTIONS = [
  { value: 0, label: "All Time" },
  { value: 30, label: "30 Days" },
  { value: 90, label: "90 Days" },
  { value: 365, label: "1 Year" },
];

const formatValue = (entry) => {
  if (entry.format === "currency") return `₹${formatNumber(entry.value)}`;
  if (entry.format === "percentage") return `${entry.value}%`;
  return entry.value;
};

const formatNumber = (num) => {
  if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
  if (num >= 100000) return (num / 100000).toFixed(1) + "L";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString("en-IN");
};

const PODIUM_CONFIG = [
  { pos: 1, height: "h-32 md:h-40", ring: "ring-amber-400", gradBg: "from-amber-400 to-yellow-500", label: "1st", size: "w-16 h-16 md:w-20 md:h-20", textSize: "text-lg md:text-xl", order: "order-2" },
  { pos: 2, height: "h-24 md:h-28", ring: "ring-gray-300", gradBg: "from-gray-300 to-gray-400", label: "2nd", size: "w-13 h-13 md:w-16 md:h-16", textSize: "text-base md:text-lg", order: "order-1" },
  { pos: 3, height: "h-20 md:h-24", ring: "ring-amber-600", gradBg: "from-amber-600 to-amber-700", label: "3rd", size: "w-12 h-12 md:w-14 md:h-14", textSize: "text-base", order: "order-3" },
];

export default function LeaderboardPage() {
  const { token, isSuperAdmin } = useAuth();
  const [category, setCategory] = useState("referrals_given");
  const [days, setDays] = useState(0);
  const [chapterId, setChapterId] = useState("");
  const [entries, setEntries] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ category });
      if (days > 0) params.append("days", days);
      if (chapterId) params.append("chapter_id", chapterId);
      const res = await axios.get(`${API}/leaderboard?${params}`, { headers });
      setEntries(res.data.entries || []);
      setChapters(res.data.chapters || []);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
    } finally {
      setLoading(false);
    }
  }, [token, category, days, chapterId]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  usePolling(fetchLeaderboard, 15000);

  const activeCat = CATEGORIES.find(c => c.key === category);
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <>
      <SEO title="Leaderboard" description="See top-performing BCH members — ranked by referrals, business volume, visitors, and attendance." path="/leaderboard" noIndex />
      <div className="space-y-6 md:space-y-8" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3" data-testid="leaderboard-title">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                <Trophy className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />
              </div>
              Leaderboard
            </h1>
            <p className="text-muted text-sm mt-1 ml-[52px] md:ml-[60px]">Top performers across your network</p>
          </div>
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="leaderboard-filters">
            <div className="flex items-center gap-1.5 bg-card border border-[var(--border-color)] rounded-xl px-3 py-2">
              <Funnel className="w-4 h-4 text-muted" weight="duotone" />
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="bg-transparent text-primary text-sm focus:outline-none cursor-pointer"
                data-testid="leaderboard-period-filter"
              >
                {PERIOD_OPTIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            {isSuperAdmin && (
            <div className="flex items-center gap-1.5 bg-card border border-[var(--border-color)] rounded-xl px-3 py-2">
              <Buildings className="w-4 h-4 text-muted" weight="duotone" />
              <select
                value={chapterId}
                onChange={(e) => setChapterId(e.target.value)}
                className="bg-transparent text-primary text-sm focus:outline-none cursor-pointer max-w-[140px]"
                data-testid="leaderboard-chapter-filter"
              >
                <option value="">All Chapters</option>
                {chapters.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))}
              </select>
            </div>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="leaderboard-tabs">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = category === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => setCategory(cat.key)}
                data-testid={`leaderboard-tab-${cat.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r " + cat.gradient + " text-white shadow-md"
                    : "bg-card border border-[var(--border-color)] text-secondary hover:text-primary hover:border-[var(--border-color)]"
                }`}
              >
                <Icon className="w-4 h-4" weight={isActive ? "fill" : "duotone"} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-12 text-center" data-testid="leaderboard-empty">
            <Trophy className="w-16 h-16 text-muted/20 mx-auto mb-4" weight="duotone" />
            <p className="text-primary font-semibold text-lg">No data yet</p>
            <p className="text-muted text-sm mt-1">Rankings will appear as members contribute to the network</p>
          </div>
        ) : (
          <>
            {/* Podium — Top 3 */}
            {top3.length >= 2 && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6 md:p-8" data-testid="leaderboard-podium">
                <div className="flex items-end justify-center gap-3 md:gap-6 max-w-xl mx-auto">
                  {PODIUM_CONFIG.map((cfg) => {
                    const entry = top3[cfg.pos - 1];
                    if (!entry) return <div key={cfg.pos} className={`flex-1 ${cfg.order}`} />;
                    const CatIcon = activeCat.icon;
                    return (
                      <div key={cfg.pos} className={`flex-1 flex flex-col items-center ${cfg.order}`}>
                        {/* Avatar */}
                        <div className={`${cfg.size} rounded-2xl bg-gradient-to-br ${cfg.gradBg} flex items-center justify-center ring-4 ${cfg.ring} ring-offset-2 ring-offset-[var(--bg-card)] mb-3 shadow-lg`}>
                          <span className={`text-white font-bold ${cfg.textSize}`}>
                            {entry.name?.charAt(0)}
                          </span>
                        </div>
                        <p className="text-primary font-semibold text-sm text-center truncate w-full">{entry.name}</p>
                        <p className="text-muted text-[11px] text-center truncate w-full">{entry.chapter_name}</p>
                        {entry.business_name && (
                          <p className="text-muted text-[10px] text-center truncate w-full flex items-center justify-center gap-1 mt-0.5">
                            <Briefcase className="w-3 h-3" weight="duotone" />
                            {entry.business_name}
                          </p>
                        )}
                        {/* Value badge */}
                        <div className={`mt-2 px-3 py-1.5 rounded-xl bg-gradient-to-r ${cfg.gradBg} text-white text-sm font-bold shadow-md flex items-center gap-1.5`}>
                          <CatIcon className="w-3.5 h-3.5" weight="fill" />
                          {formatValue(entry)}
                        </div>
                        {/* Podium bar */}
                        <div className={`w-full ${cfg.height} rounded-t-xl bg-gradient-to-t ${cfg.gradBg} mt-3 flex items-start justify-center pt-2`}>
                          <span className="text-white/90 font-bold text-xs">{cfg.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Rankings Table */}
            <div className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid="leaderboard-table">
              <div className="p-5 md:p-6 border-b border-[var(--border-color)] flex items-center justify-between">
                <h2 className="text-base md:text-lg font-semibold text-primary flex items-center gap-2">
                  <Medal className="w-5 h-5 text-bch-gold" weight="fill" />
                  Full Rankings
                </h2>
                <span className="text-muted text-xs">{entries.length} member{entries.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="divide-y divide-[var(--border-color)]">
                {entries.map((entry, i) => {
                  const CatIcon = activeCat.icon;
                  return (
                    <div
                      key={entry.id}
                      className="flex items-center gap-3 md:gap-4 px-5 md:px-6 py-4 hover:bg-surface transition-colors"
                      data-testid={`leaderboard-entry-${i}`}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        i === 0 ? "bg-gradient-to-br from-amber-400 to-yellow-500" :
                        i === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400" :
                        i === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700" :
                        "bg-surface border border-[var(--border-color)]"
                      }`}>
                        <span className={`text-xs font-bold ${i < 3 ? "text-white" : "text-muted"}`}>{i + 1}</span>
                      </div>
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeCat.gradient} flex items-center justify-center shrink-0`}>
                        <span className="text-white text-sm font-bold">{entry.name?.charAt(0)}</span>
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-primary text-sm font-semibold truncate">{entry.name}</p>
                        <div className="flex items-center gap-2 text-muted text-xs">
                          <span className="truncate">{entry.chapter_name}</span>
                          {entry.business_name && (
                            <>
                              <span className="text-[var(--border-color)]">|</span>
                              <span className="truncate hidden sm:inline">{entry.business_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Value */}
                      <div className="text-right shrink-0">
                        <p className={`font-mono text-sm md:text-base font-bold ${activeCat.color}`}>{formatValue(entry)}</p>
                        <p className="text-muted text-[10px] md:text-xs">{entry.secondary}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
